import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { prettyDate } from "@/components/ProviderFilters";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SocialIcons } from "@/components/SocialLinks";
import { contractPdf } from "@/lib/contractPdf";
import { SignContractDialog } from "@/components/SignContractDialog";
import { MepaBadge, CpvList, SplitPaymentBox } from "@/components/PaWidgets";
import { MepaButton } from "@/components/MepaDialog";
import { PaKitBox } from "@/components/PaKitBox";
import { PostersSection, GuestSlogan } from "@/components/ProfileExtras";
import { eur } from "@/lib/pa";
import { Star, MapPin, Calendar, Lock, MessageSquare, Calculator, FileText, CheckCircle2, ShieldCheck, ExternalLink, PlayCircle, BadgeCheck, Flag } from "lucide-react";

export default function ArtistDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const isPA = user?.role === "public_entity";
  const paViewer = ["public_entity", "business", "admin"].includes(user?.role);
  const [ipa, setIpa] = useState(user?.ipaCode || "");
  useEffect(() => { setIpa(user?.ipaCode || ""); }, [user?.ipaCode]);
  const ipaOk = () => {
    if (!isPA || /^[A-Z0-9]{6}$/.test(ipa)) return true;
    toast.error("Inserisci il Codice Univoco Ufficio (IPA): 6 caratteri alfanumerici");
    return false;
  };
  const quoteBody = () => ({ artistId: id, distanceKm: distance, optionType, ...(isPA ? { ipaCode: ipa } : {}) });
  const [a, setA] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [distance, setDistance] = useState(90);
  const [quote, setQuote] = useState(null);
  const [optionType, setOptionType] = useState(null);
  const [ev, setEv] = useState({ eventDate:"", eventTime:"", eventLocation:"", eventType:"" });
  useEffect(() => { setQuote(null); }, [distance, optionType]);
  const [newReview, setNewReview] = useState({ rating:5, comment:"" });
  const [signOpen, setSignOpen] = useState(false);
  const [signQuote, setSignQuote] = useState(null);
  const [params] = useSearchParams();
  const wantsReview = params.get("recensione") === "1";
  const futureDates = (a?.availabilityCalendar || []).filter(d=>d>=new Date().toISOString().slice(0,10));
  useEffect(() => {
    if (!wantsReview || !a) return;
    setTimeout(() => document.getElementById("recensione")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [wantsReview, a]);
  useEffect(() => {
    const d = params.get("data");
    if (d && futureDates.includes(d)) { setEv((p) => ({ ...p, eventDate: d })); }
  }, [a]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    api.get(`/profile/${id}`).then(r=>setA(r.data));
    api.get(`/reviews/${id}`).then(r=>setReviews(r.data));
  },[id]);

  const calcQuote = async () => {
    if (!user) { toast.error("Accedi per calcolare il preventivo"); return; }
    if (!ipaOk()) return;
    try {
      const r = await api.post("/quotes", quoteBody());
      setQuote(r.data);
      if (isPA && r.data.ipaCode !== user.ipaCode) refresh();
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const openSign = async () => {
    if (!user) { toast.error("Accedi per prenotare"); return; }
    if (!ev.eventDate || !ev.eventLocation || !ev.eventType) { toast.error("Compila data, luogo e tipo evento"); return; }
    if (!ipaOk()) return;
    try {
      const r = await api.post("/quotes", quoteBody());
      setQuote(r.data); setSignQuote(r.data); setSignOpen(true);
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const confirmAndSign = async ({ signature, signerName, notes, cig, cup }) => {
    try {
      const r = await api.post("/bookings", { ...quoteBody(), ...ev, eventTime: ev.eventTime || null, notes, signature, signerName, cig, cup });
      contractPdf(r.data);
      setSignOpen(false);
      toast.success("Contratto firmato e inviato: l'artista ha 24 ore per controfirmare");
      api.get(`/profile/${id}`).then((p) => setA(p.data));
      setEv((p) => ({ ...p, eventDate: "" }));
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore prenotazione"); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { targetUserId: id, ...newReview });
      toast.success("Recensione inviata");
      const r = await api.get(`/reviews/${id}`); setReviews(r.data);
      const p = await api.get(`/profile/${id}`); setA(p.data);
      setNewReview({ rating:5, comment:"" });
    } catch(err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  useEffect(() => {
    if (!a) return;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
    m.content = a.indexable ? "index, follow" : "noindex, nofollow";
    return () => { m.content = "index, follow"; };
  }, [a]);

  const reportProfile = async () => {
    const reason = window.prompt("Descrivi il motivo della segnalazione (min. 5 caratteri)");
    if (!reason) return;
    try { await api.post("/reports", { targetUserId: id, reason }); toast.success("Segnalazione inviata all'amministrazione"); }
    catch(err) { toast.error(err?.response?.data?.detail?.[0]?.msg ? "Motivo troppo breve" : (err?.response?.data?.detail || "Errore")); }
  };

  if (!a) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-slate-500">Caricamento…</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden">
            <img src={mediaUrl(a.media?.photos?.[0]) || "https://images.unsplash.com/photo-1569261116753-5c615c9e18dd?w=1200"} alt="" className="w-full h-full object-cover"/>
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-2">
              {a.isRecommendedEvent && <span className="chip-gold">Consigliati</span>}
              {(a.disciplines?.length ? a.disciplines : a.categories?.length ? a.categories : [a.macroCategory]).filter(Boolean).map((d,i)=><span key={i} className="chip-navy">{d}</span>)}
            </div>
            <h1 className="font-display text-4xl font-bold flex items-center gap-2 flex-wrap">{a.companyName}{a.mepaEnabled && <MepaBadge note={a.mepaNote}/>}</h1>
            <div className="flex items-center gap-4 text-sm text-slate-600 mt-2">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4"/>{a.baseLocation}</span>
              {a.rating!==null && a.rating!==undefined && <span className="flex items-center gap-1 text-gold font-semibold"><Star className="w-4 h-4 fill-gold"/>{a.rating?.toFixed(1)} ({a.reviewCount} recensioni)</span>}
              {a.performanceDuration && <span>⏱ {a.performanceDuration}</span>}
            </div>
            <CpvList cpv={a.cpv} />
            {a.bio && <p data-testid="profile-bio" className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{a.bio}</p>}
            {a.guestView && <GuestSlogan/>}
            <SocialIcons socials={a.socials} />
          </div>
          {a.media?.photos?.length>1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="profile-photos">{a.media.photos.slice(1).map((p,i)=><a key={i} href={mediaUrl(p)} target="_blank" rel="noreferrer"><img src={mediaUrl(p)} alt="" loading="lazy" className="aspect-square w-full object-cover rounded-xl hover:opacity-90 transition-opacity"/></a>)}</div>
          )}
          {a.media?.videos?.length>0 && (
            <div>
              <div className="overline text-gold mb-3">Video performance</div>
              <div className="space-y-4" data-testid="profile-videos">{a.media.videos.map((v,i)=><video key={i} controls preload="metadata" src={mediaUrl(v)} className="w-full rounded-2xl bg-navy"/>)}</div>
            </div>
          )}
          <PostersSection ownerId={a.id}/>
          {a.externalReviews?.length>0 && (
            <div>
              <div className="overline text-gold mb-3">Recensioni esterne</div>
              <div className="flex flex-wrap gap-3">
                {a.externalReviews.map((er,i)=>(
                  <a key={i} href={er.url} target="_blank" rel="noreferrer" className="card-premium px-4 py-3 flex items-center gap-3">
                    <ExternalLink className="w-4 h-4 text-gold"/>
                    <div>
                      <div className="font-semibold text-sm">{er.platform}</div>
                      <div className="text-xs text-gold flex items-center gap-1"><Star className="w-3 h-3 fill-gold"/>{er.rating}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {a.availabilityCalendar?.length>0 && (
            <div className="card-premium p-6">
              <div className="overline text-gold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4"/>Disponibilità</div>
              <div className="flex flex-wrap gap-2">{a.availabilityCalendar.filter(d=>d>=new Date().toISOString().slice(0,10)).map((d)=><button key={d} type="button" data-testid={`avail-${d}`} onClick={()=>setEv((p)=>({...p, eventDate:d}))} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${ev.eventDate===d?"bg-gold text-navy border-gold":"border-slate-300 hover:border-gold"}`}>{prettyDate(d)}</button>)}</div>
              <p className="text-xs text-slate-500 mt-3">Clicca una data libera per usarla nel preventivo.</p>
            </div>
          )}

          <div>
            <div className="overline text-gold mb-3">Recensioni verificate</div>
            {reviews.length===0 && <div className="text-sm text-slate-500" data-testid="reviews-empty">{a.guestView ? "Iscriviti gratuitamente per leggere le recensioni." : "Nessuna recensione pubblicata."}</div>}
            <div className="space-y-3">
              {reviews.map((r,i)=>(
                <div key={i} className="card-premium p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-gold font-semibold flex items-center gap-1"><Star className="w-4 h-4 fill-gold"/>{r.rating}/5</span>
                    <span className="chip-navy">{r.fromRole}</span>
                  </div>
                  <p className="text-sm text-slate-700">{r.comment}</p>
                  {r.reply && <div className="mt-3 border-l-2 border-gold pl-3 text-sm" data-testid={`review-reply-${r.id}`}><div className="text-xs font-semibold text-navy">Risposta di {a.companyName}</div><p className="text-slate-600">{r.reply.text}</p></div>}
                </div>
              ))}
            </div>
            {!user && (
              <div className="mt-4 card-premium p-4 text-sm flex items-center justify-between gap-3 flex-wrap" id="recensione" data-testid="review-login-cta">
                <span className="text-slate-600">Hai lavorato con questo artista? Accedi per lasciare la tua recensione.</span>
                <Link to={`/login?next=${encodeURIComponent(`/artista/${id}?recensione=1`)}`} className="btn-pill-gold text-sm">Accedi e recensisci</Link>
              </div>
            )}
            {user && !["artist","supplier"].includes(user.role) && (
              <form id="recensione" onSubmit={submitReview} className={`mt-4 card-premium p-4 space-y-3 ${wantsReview ? "ring-2 ring-gold" : ""}`}>
                <div className="overline text-slate-500">Aggiungi la tua recensione</div>
                <select data-testid="review-rating" value={newReview.rating} onChange={(e)=>setNewReview({...newReview, rating:parseInt(e.target.value)})} className="border border-slate-300 rounded-xl px-3 py-2 text-sm">
                  {[5,4,3,2,1].map(x=><option key={x} value={x}>{x} stelle</option>)}
                </select>
                <textarea data-testid="review-comment" value={newReview.comment} onChange={(e)=>setNewReview({...newReview, comment:e.target.value})} placeholder="Commento…" className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm" rows={3}/>
                <button data-testid="review-submit" className="btn-pill-gold text-sm">Invia recensione</button>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar: Quote */}
        <div className="space-y-5">
          <div className="bg-navy text-white p-6 rounded-2xl shadow-xl border border-gold/20">
            <div className="flex items-center justify-between pb-4 border-b border-navy-700">
              <h3 className="text-xl font-bold text-gold flex items-center gap-2"><Calculator className="w-5 h-5"/>Calcolo Preventivo</h3>
              <span className="chip-gold">Ufficiale PA/B2B</span>
            </div>
            {a.baseFeeNet!==null && a.baseFeeNet!==undefined && !a.quoteEnabled ? (
              <div className="py-8 text-center" data-testid="quote-disabled">
                <Lock className="w-10 h-10 text-gold mx-auto mb-3"/>
                <p className="text-sm text-slate-300">Questo profilo non ha ancora attivato il preventivo automatico e la prenotazione diretta.</p>
                <p className="text-xs text-slate-400 mt-2">Compenso base indicativo: €{a.baseFeeNet.toFixed(2)}</p>
              </div>
            ) : a.baseFeeNet===null || a.baseFeeNet===undefined ? (
              <div className="py-8 text-center">
                <Lock className="w-10 h-10 text-gold mx-auto mb-3"/>
                <p className="text-sm text-slate-300">{user?.role==="citizen" ? "Come privato, il Portale gestisce per te preventivo e prenotazione." : "Prezzi riservati ai Committenti registrati"}</p>
                {user?.role==="citizen" ? (
                  <Link to="/dashboard" data-testid="citizen-broker-cta" className="btn-pill-gold mt-4 inline-flex text-xs">Invia richiesta al Portale</Link>
                ) : !user || user.role==="artist" || user.role==="supplier" ? (
                  <Link to="/registrati" className="btn-pill-gold mt-4 inline-flex text-xs">Iscriviti come Ente / Business</Link>
                ) : null}
              </div>
            ) : (
              <>
                <div className="py-4 space-y-3 text-sm">
                  {isPA && (
                    <div>
                      <label className="text-xs text-slate-300">Codice Univoco Ufficio IPA <span className="text-gold">*</span></label>
                      <input data-testid="quote-ipa" value={ipa} maxLength={6} onChange={(e)=>setIpa(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="es. UF1234" className="w-full mt-1 bg-navy-800 border border-navy-700 rounded-lg px-3 py-2 font-mono tracking-wider"/>
                      <p className="text-[11px] text-slate-400 mt-1">Obbligatorio per la fatturazione elettronica PA.{user?.splitPayment ? " Ente soggetto a Split Payment." : ""}</p>
                    </div>
                  )}
                  <div className="flex justify-between"><span>Compenso base</span><span className="font-semibold">€{a.baseFeeNet.toFixed(2)}</span></div>
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-slate-300">Trasferta A/R (km)</label>
                    <input data-testid="distance-km" type="number" value={distance} onChange={(e)=>setDistance(parseInt(e.target.value)||0)} className="w-20 bg-navy-800 border border-navy-700 rounded-lg px-2 py-1 text-right"/>
                  </div>
                  <div className="flex justify-between text-slate-400" data-testid="travel-line"><span>Trasferta ({Math.max(0, distance - (a.travelIncludedKm || 0))} km × €{(a.travelRatePerKm ?? 0.4).toFixed(2)}{a.travelIncludedKm ? ` · primi ${a.travelIncludedKm} km inclusi` : ""})</span><span>€{(Math.max(0, distance - (a.travelIncludedKm || 0)) * (a.travelRatePerKm ?? 0.4)).toFixed(2)}</span></div>
                  <div className="text-[11px] text-slate-400" data-testid="coverage-line">Raggio d'azione: {a.coverageAllItaly ? "Tutta Italia" : `${a.coverageMaxKm} km dalla sede`}</div>
                  {quote && (
                    <>
                      {quote.optionFee>0 && <div className="flex justify-between text-slate-400"><span>Opzione 72h</span><span>€{quote.optionFee.toFixed(2)}</span></div>}
                      <div className="border-t border-navy-700 pt-3 flex justify-between"><span>Imponibile</span><span>€{quote.subtotalNet.toFixed(2)}</span></div>
                      <div className="flex justify-between text-slate-400"><span>{quote.vatLabel || "IVA 22%"}</span><span>€{quote.vat.toFixed(2)}</span></div>
                      <div className="flex justify-between text-lg font-bold text-gold border-t border-navy-700 pt-3"><span>Totale</span><span>€{quote.totalGross.toFixed(2)}</span></div>
                      <SplitPaymentBox q={quote} />
                    </>
                  )}
                </div>
                <div className="mt-2 space-y-2 text-sm">
                  {futureDates.length>0 ? (
                    <select data-testid="event-date" value={ev.eventDate} onChange={(e)=>setEv({...ev, eventDate:e.target.value})} className="w-full bg-navy-800 border border-navy-700 rounded-lg px-3 py-2">
                      <option value="">Scegli una data libera</option>
                      {a.availabilityCalendar.filter(d=>d>=new Date().toISOString().slice(0,10)).map(d=><option key={d} value={d}>{prettyDate(d)}</option>)}
                    </select>
                  ) : (
                    <div data-testid="event-date-none" className="w-full bg-navy-800 border border-navy-700 rounded-lg px-3 py-2 text-slate-200 text-xs">Calendario pieno: nessuna data libera indicata dall'artista</div>
                  )}
                  <label className="block text-xs text-slate-400 -mb-1">Orario evento (facoltativo)</label>
                  <input data-testid="event-time" type="time" aria-label="Orario evento" title="Orario evento (facoltativo)" value={ev.eventTime} onChange={(e)=>setEv({...ev, eventTime:e.target.value})} className="w-full bg-navy-800 border border-navy-700 rounded-lg px-3 py-2"/>
                  <input data-testid="event-location" placeholder="Luogo evento" value={ev.eventLocation} onChange={(e)=>setEv({...ev, eventLocation:e.target.value})} className="w-full bg-navy-800 border border-navy-700 rounded-lg px-3 py-2"/>
                  <input data-testid="event-type" placeholder="Tipo evento (es. Festa patronale)" value={ev.eventType} onChange={(e)=>setEv({...ev, eventType:e.target.value})} className="w-full bg-navy-800 border border-navy-700 rounded-lg px-3 py-2"/>
                </div>
                <div className="mt-4 space-y-2">
                  <button data-testid="quote-calc" onClick={calcQuote} className="w-full py-2.5 px-4 rounded-xl font-medium text-xs bg-navy-800 border border-navy-700 hover:border-gold">Calcola Preventivo</button>
                  <button data-testid="option-free-24h" disabled={!futureDates.length} onClick={()=>setOptionType(optionType==="free_24h"?null:"free_24h")} className={`w-full py-2.5 px-4 rounded-xl font-medium text-xs border flex items-center justify-center gap-2 ${optionType==="free_24h"?"bg-navy-800 border-gold text-gold":"bg-navy-800/50 border-navy-700"}`}><CheckCircle2 className="w-4 h-4"/>Opziona GRATIS 24h</button>
                  <button data-testid="option-paid-72h" disabled={!futureDates.length} onClick={()=>setOptionType(optionType==="paid_72h"?null:"paid_72h")} className={`w-full py-2.5 px-4 rounded-xl font-medium text-xs border flex items-center justify-center gap-2 ${optionType==="paid_72h"?"bg-gold/20 border-gold text-gold":"bg-navy-800/50 border-navy-700"}`}><ShieldCheck className="w-4 h-4"/>Opziona 72h (€10)</button>
                  <button data-testid="book-and-pdf" disabled={!futureDates.length} onClick={openSign} className="w-full mt-3 py-3 px-4 bg-gold hover:bg-gold-600 text-navy font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"><FileText className="w-5 h-5"/>Conferma e firma contratto</button>
                  <p className="text-[11px] text-slate-400 text-center">Dopo la tua firma l'artista ha 24 ore per controfirmare o rifiutare.</p>
                  {signQuote && (
                    <SignContractDialog open={signOpen} onOpenChange={setSignOpen} testPrefix="client-sign" withNotes
                      paFields={["public_entity","business"].includes(user?.role)} cigRequired={isPA}
                      title="Firma il contratto" description={`Contratto con ${a.companyName}. Firmando invii la proposta vincolante all'artista.`}
                      summary={[["Artista", a.companyName], ["Data", prettyDate(ev.eventDate)], ["Orario", ev.eventTime || "da concordare"], ["Luogo", ev.eventLocation], ["Evento", ev.eventType], ["Compenso base", `€${signQuote.baseFeeNet.toFixed(2)}`], [`Trasferta ${signQuote.travelKm} km`, `€${signQuote.travelCost.toFixed(2)}`], ...(signQuote.optionFee>0 ? [["Opzione 72h", `€${signQuote.optionFee.toFixed(2)}`]] : []), ["Imponibile", `€${signQuote.subtotalNet.toFixed(2)}`], ["IVA", `${signQuote.vatLabel} · €${signQuote.vat.toFixed(2)}`], ["Totale IVA incl.", `€${signQuote.totalGross.toFixed(2)}`], ...(signQuote.splitPayment ? [["Split Payment · all'operatore", eur(signQuote.payableToOperator)], ["Split Payment · IVA all'Erario", eur(signQuote.vatToErario)]] : []), ...(isPA ? [["Codice IPA", ipa]] : [])]}
                      confirmLabel="Firma e invia all'artista" onConfirm={confirmAndSign} />
                  )}
                </div>
              </>
            )}
          </div>

          {paViewer && <MepaButton provider={a} distance={distance} optionType={optionType} ev={ev} />}
          <PaKitBox provider={a} />
          {user && !a.contactHidden && a.chatEnabled && (
            <button data-testid="open-chat" onClick={()=>nav(`/chat?to=${a.id}`)} className="btn-pill-outline w-full justify-center"><MessageSquare className="w-4 h-4"/>Contatta via Chat</button>
          )}
          {user && user.id !== a.id && (
            <button data-testid="report-profile" onClick={reportProfile} className="w-full text-xs text-slate-500 hover:text-red-600 flex items-center justify-center gap-1 py-2"><Flag className="w-3.5 h-3.5"/>Segnala profilo</button>
          )}
        </div>
      </div>
    </div>
  );
}

